<script setup lang="ts">
import {onMounted, useTemplateRef, watch} from 'vue';
import {SvgIcon} from '../svg.ts';
import ActionStatusIcon from './ActionStatusIcon.vue';
import {formatDatetime} from '../utils/time.ts';
import {createLogLineMessage} from './ActionRunView.ts';
import type {LogItem, Step} from './ActionRunLogModel.ts';
import type {ActionsStatus} from '../modules/helmfold-actions.ts';

const props = defineProps<{
  item: LogItem;
  step?: Step; // required when item.kind === 'step-header'
  startedAt: number;
  expanded: boolean;
  // deep-link target line, driving the `target-highlight` class below; see the
  // `.job-log-line.target-highlight` rule in this component's <style> for the visual effect.
  highlighted: boolean;
  // current search-match indicator, independent of `highlighted` (deep-link target) -- see the
  // `.search-match-highlight` rule below. Two channels, not one: reusing `highlighted` for both
  // features was tried and rejected (clicking any line-number anchor while search is open would
  // silently steal the highlight) -- see the design spec's "Match identity & highlighting".
  isSearchMatch: boolean;
  isDoneRun: boolean;
  // step-header rows only: this step's first log fetch is still in flight (its cursor is still
  // null), which is what the header spinner below indicates -- orthogonal to `isDoneRun`.
  loading: boolean;
  locale: Record<string, any>;
}>();

const emit = defineEmits<{
  toggleStep: [step: number];
  toggleGroup: [key: string];
  copyStep: [step: number, event: MouseEvent];
}>();

const msgEl = useTemplateRef<HTMLSpanElement>('msgEl');

function renderMessage() {
  if (props.item.kind !== 'line' || !msgEl.value) return;
  const rendered = createLogLineMessage(props.item.line, props.item.cmd);
  msgEl.value.replaceChildren(...rendered.childNodes);
  msgEl.value.className = rendered.className;
}

onMounted(renderMessage);
watch(() => props.item.key, renderMessage, {flush: 'post'});

function isExpandableStatus(status: ActionsStatus) {
  return ['success', 'running', 'failure', 'cancelled'].includes(status);
}
</script>

<template>
  <div
    v-if="item.kind === 'step-header' && step"
    class="job-step-summary"
    role="button" tabindex="0" :aria-expanded="expanded"
    :class="[expanded ? 'selected' : '', isExpandableStatus(step.status) && 'step-expandable', isSearchMatch && 'search-match-highlight']"
    @click="isExpandableStatus(step.status) && emit('toggleStep', item.step)"
    @keydown.enter="isExpandableStatus(step.status) && emit('toggleStep', item.step)"
  >
    <SvgIcon v-if="isDoneRun && expanded && loading" name="helmfold-running" class="rotate-clockwise"/>
    <SvgIcon
      v-else name="octicon-chevron-right" class="tw-mr-2 step-summary-chevron"
      :class="{'tw-invisible': !isExpandableStatus(step.status), collapsed: !expanded}"
    />
    <ActionStatusIcon :status="step.status" icon-variant="circle-fill"/>
    <span class="step-summary-msg gt-ellipsis">{{ step.summary }}</span>
    <button
      v-if="isExpandableStatus(step.status)"
      class="btn interact-fg step-copy-btn"
      :aria-label="locale.copyOutput"
      :data-tooltip-content="locale.copyOutput"
      @click.stop="emit('copyStep', item.step, $event)"
    >
      <SvgIcon name="octicon-copy" :size="14"/>
    </button>
    <span class="step-summary-duration">{{ step.duration }}</span>
  </div>
  <div
    v-else-if="item.kind === 'line'"
    :id="`jobstep-${item.step}-${item.line.index}`"
    class="job-log-line"
    :class="[item.cmd?.name ? `log-line-${item.cmd.name}` : '', highlighted && 'target-highlight', isSearchMatch && 'search-match-highlight']"
  >
    <a class="line-num muted" :href="`#jobstep-${item.step}-${item.line.index}`">{{ item.line.index }}</a>
    <span class="log-time-stamp">{{ formatDatetime(item.line.timestamp * 1000) }}</span>
    <span
      v-if="item.cmd?.name === 'group'"
      class="log-msg-group-toggle" :class="{collapsed: !expanded}"
      role="button" tabindex="0" :aria-expanded="expanded"
      @click="emit('toggleGroup', item.key)"
      @keydown.enter="emit('toggleGroup', item.key)"
    >
      <span ref="msgEl"/>
    </span>
    <span v-else ref="msgEl"/>
    <span class="log-time-seconds">{{ Math.floor(item.line.timestamp - startedAt) }}s</span>
  </div>
</template>

<style scoped>
.job-step-summary {
  padding: 5px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: var(--border-radius);
}

.job-step-summary.step-expandable {
  cursor: pointer;
}

.job-step-summary.step-expandable:hover {
  color: var(--color-console-fg);
  background: var(--color-console-hover-bg);
}

.job-step-summary .step-summary-chevron {
  transition: transform 0.1s ease;
}

.job-step-summary.selected .step-summary-chevron {
  transform: rotate(90deg);
}

.job-step-summary .step-summary-msg {
  flex: 1;
}

.job-step-summary .step-copy-btn {
  visibility: hidden;
  margin: 0 4px;
}

.job-step-summary:hover .step-copy-btn,
.job-step-summary.selected .step-copy-btn {
  visibility: visible;
}

@media (hover: none) {
  .job-step-summary:focus-within .step-copy-btn {
    visibility: visible;
  }
}

.job-step-summary.selected {
  color: var(--color-console-fg);
  background-color: var(--color-console-active-bg);
  /* position/top are inert under virtualization: the virtualizer sets `position: absolute` as an
     inline style on the row, and there is no in-flow containing block for sticky to work within.
     Making the selected step header stick again needs a separately-rendered sticky overlay row;
     tracked as a follow-up, not fixed here. */
  position: sticky;
  top: 60px;
  /* workaround ansi_up issue related to faintStyle generating a CSS stacking context via `opacity`
     inline style which caused such elements to render above the .job-step-summary header. */
  z-index: 1;
}

/* placed after .step-expandable:hover and .selected above (and repeated with an explicit :hover
   variant) so a search match on a step's summary stays visible even while selected or hovered --
   same specificity (two classes) as those rules, so source order is what makes this win. */
.job-step-summary.search-match-highlight {
  background-color: var(--color-highlight-bg);
  outline: 1px solid var(--color-highlight-fg);
  outline-offset: -1px;
}

.job-step-summary.search-match-highlight:hover {
  background-color: var(--color-highlight-bg);
}

/* the monospace font and 12px size used to come from the `.job-step-logs` wrapper that held each
   step's lines before virtualization; that wrapper is gone, so scope them to the line rows here --
   deliberately NOT on .job-step-summary, which was never monospace. The wrapper's `margin: 8px 0`
   has no per-row equivalent (vertical margins are excluded from the virtualizer's row
   measurement), so the block spacing lives on .job-step-container in ActionRunJobView.vue. */
.job-log-line {
  display: flex;
  font-family: var(--fonts-monospace);
  font-size: 12px;
}

.job-log-line:hover,
.job-log-line:target {
  background-color: var(--color-console-hover-bg);
}

.job-log-line:target {
  scroll-margin-top: 95px;
}

.job-log-line .log-time-stamp,
.job-log-line .log-time-seconds {
  display: none;
}

.log-line-show-timestamps .job-log-line .log-time-stamp {
  display: inline;
}

.log-line-show-seconds .job-log-line .log-time-seconds {
  display: inline;
}

/* class names 'log-time-seconds' and 'log-time-stamp' are used in the method toggleTimeDisplay */
.job-log-line .line-num,
.job-log-line .log-time-seconds {
  width: 48px;
  color: var(--color-text-light-3);
  text-align: right;
  user-select: none;
}

.job-log-line:target > .line-num {
  color: var(--color-primary);
  text-decoration: underline;
}

.log-time-seconds {
  padding-right: 2px;
}

.job-log-line .log-time-stamp {
  color: var(--color-text-light-3);
  margin-left: 12px;
  white-space: nowrap;
}

.job-log-line .log-msg {
  flex: 1;
  white-space: break-spaces;
  margin-left: 12px;
  overflow-wrap: anywhere;
}

.job-log-line .log-cmd-command {
  color: var(--color-ansi-blue);
}

.log-line-error {
  background: var(--color-error-bg);
}

.log-line-warning {
  background: var(--color-warning-bg);
}

.log-line-notice {
  background: var(--color-info-bg);
}

.log-line-debug {
  background: var(--color-secondary-alpha-30);
}

.log-msg-group-toggle {
  cursor: pointer;
  display: contents; /* keep the child .log-msg's flex layout as if this wrapper weren't there */
}

.log-msg-group-toggle .log-msg::before {
  content: "";
  display: inline-block;
  vertical-align: middle;
  margin-top: -2.5px;
  margin-right: 8px;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-left: 6px solid var(--color-text-light-3);
  transition: transform 0.1s ease;
  transform: rotate(90deg);
}

.log-msg-group-toggle.collapsed .log-msg::before {
  transform: none;
}

.job-log-line.target-highlight {
  background-color: var(--color-console-hover-bg);
}

.job-log-line.search-match-highlight {
  background-color: var(--color-highlight-bg);
  outline: 1px solid var(--color-highlight-fg);
  outline-offset: -1px;
}
</style>

<style> /* eslint-disable-line vue-scoped-css/enforce-style-type */
/* .log-msg's content (the message text, .log-msg-label, and any auto-linked <a> tags) is built by
   createLogLineMessage()/renderAnsiInto() via raw DOM APIs, not by this component's template, so
   it never carries this component's scope attribute -- these rules must stay global, matching the
   log-msg-label handling in the DOM-manipulation-path styles this replaces. */
.log-msg a {
  color: var(--color-console-link) !important;
  text-decoration: underline;
}

.log-msg-label {
  font-weight: var(--font-weight-semibold);
}

.log-cmd-error > .log-msg-label {
  color: var(--color-error-text);
}

.log-cmd-warning > .log-msg-label {
  color: var(--color-warning-text);
}

.log-cmd-notice > .log-msg-label {
  color: var(--color-info-text);
}

.log-cmd-debug > .log-msg-label {
  color: var(--color-violet);
}
</style>
